import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Switch,
  Button, Tabs, Tab, Divider, Alert, CircularProgress, InputAdornment, Chip, MenuItem,
} from '@mui/material';
import { Save, CheckCircle, Cancel, Link as LinkIcon, LinkOff } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { settings, bot, zerodha as zerodhaApi, auth, notifications as notifApi } from '../services/api';

function SettingField({ label, settingKey, value, type = 'text', onChange, description, unit }) {
  if (type === 'boolean') {
    const isChecked = value === true || value === 'true' || value === 1 || value === '1';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
          {description && <Typography variant="caption" color="text.secondary">{description}</Typography>}
        </Box>
        <Switch checked={Boolean(isChecked)} onChange={(e) => onChange(settingKey, e.target.checked)} color="primary" />
      </Box>
    );
  }
  return (
    <TextField
      fullWidth size="small" label={label} type={type === 'number' ? 'number' : 'text'}
      value={value !== undefined && value !== null ? value : ''} onChange={(e) => onChange(settingKey, e.target.value)}
      helperText={description}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  );
}

function AccountTab() {
  const [usernameForm, setUsernameForm] = useState({ currentPassword: '', newUsername: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingUsername, setSavingUsername] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [clearingOrders, setClearingOrders] = useState(false);

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    setSavingUsername(true);
    try {
      const res = await auth.updateUsername(usernameForm);
      toast.success(res.data.message || 'Username updated');
      setUsernameForm({ currentPassword: '', newUsername: '' });
    } catch { /* handled */ } finally { setSavingUsername(false); }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New password and confirmation do not match'); return;
    }
    setSavingPassword(true);
    try {
      const res = await auth.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success(res.data.message || 'Password updated');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch { /* handled */ } finally { setSavingPassword(false); }
  };

  const handleClearPaperEquityOrders = async () => {
    if (!window.confirm('⚠️ This will delete ALL paper equity stock orders from the database. This cannot be undone. Continue?')) {
      return;
    }
    setClearingOrders(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        toast.error('❌ Authentication token not found. Please refresh and login again.');
        setClearingOrders(false);
        return;
      }

      const res = await fetch('/api/trading/clear-paper-equity-orders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      // Check if response is HTML (error page) instead of JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        if (res.status === 401) {
          toast.error('❌ Session expired. Please login again.');
          window.location.href = '/login';
        } else if (res.status === 404) {
          toast.error('❌ Endpoint not found. Please refresh the page.');
        } else {
          toast.error(`❌ Server error (${res.status}). Please try again.`);
        }
        setClearingOrders(false);
        return;
      }

      const data = await res.json();
      if (data.success) {
        toast.success(`✅ ${data.deletedCount || 0} paper equity orders cleared!`);
      } else {
        toast.error(data.message || '❌ Failed to clear orders');
      }
    } catch (err) {
      toast.error(`❌ Error clearing orders: ${err.message}`);
      console.error('Clear orders error:', err);
    } finally {
      setClearingOrders(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2 }} component="form" onSubmit={handleUpdateUsername}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>Update Username</Typography>
            <TextField
              fullWidth size="small" margin="normal" label="New Username"
              value={usernameForm.newUsername}
              onChange={e => setUsernameForm({ ...usernameForm, newUsername: e.target.value })}
            />
            <TextField
              fullWidth size="small" margin="normal" label="Current Password" type="password"
              value={usernameForm.currentPassword}
              onChange={e => setUsernameForm({ ...usernameForm, currentPassword: e.target.value })}
            />
            <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={savingUsername || !usernameForm.newUsername || !usernameForm.currentPassword}>
              {savingUsername ? <CircularProgress size={20} color="inherit" /> : 'Update Username'}
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2 }} component="form" onSubmit={handleUpdatePassword}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>Update Password</Typography>
            <TextField
              fullWidth size="small" margin="normal" label="Current Password" type="password"
              value={passwordForm.currentPassword}
              onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            />
            <TextField
              fullWidth size="small" margin="normal" label="New Password" type="password"
              helperText="At least 8 characters"
              value={passwordForm.newPassword}
              onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            />
            <TextField
              fullWidth size="small" margin="normal" label="Confirm New Password" type="password"
              value={passwordForm.confirmPassword}
              onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            />
            <Button type="submit" variant="contained" sx={{ mt: 1 }}
              disabled={savingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}>
              {savingPassword ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 2, borderLeft: '4px solid #ff6b6b' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>🗑️ Clear Database</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Delete all paper equity stock orders. This action cannot be undone.
            </Typography>
            <Button
              variant="contained"
              color="error"
              onClick={handleClearPaperEquityOrders}
              disabled={clearingOrders}
              fullWidth
            >
              {clearingOrders ? <CircularProgress size={20} color="inherit" /> : 'Clear All Paper Equity Orders'}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState(0);
  const [allSettings, setAllSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [botMode, setBotMode] = useState('PAPER');
  const [zerodhaConnected, setZerodhaConnected] = useState(false);
  const [zerodhaChecking, setZerodhaChecking] = useState(false);
  const [zerodhaConnecting, setZerodhaConnecting] = useState(false);
  const [requestToken, setRequestToken] = useState('');
  const [isMobileDevice] = useState(() => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sRes, botRes] = await Promise.all([settings.getAll(), bot.status()]);
        if (sRes.data?.data && typeof sRes.data.data === 'object') {
          setAllSettings(sRes.data.data);
        }
        setBotMode(botRes.data?.data?.trade_mode || 'PAPER');
      } catch { /* handled */ } finally { setLoading(false); }
    };
    load();
  }, []);

  // Check Zerodha connection status when tab 6 is active
  useEffect(() => {
    if (tab === 6) checkZerodhaStatus();
  }, [tab]);

  // Also check on page load for redirect callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('zerodha') === 'connected') {
      setTab(6);
      toast.success('Zerodha connected successfully!');
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('zerodha') === 'error') {
      setTab(6);
      toast.error('Zerodha connection failed. Check your API key/secret.');
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const checkZerodhaStatus = async () => {
    setZerodhaChecking(true);
    try {
      const res = await zerodhaApi.status();
      setZerodhaConnected(res.data?.data?.connected || false);
    } catch { setZerodhaConnected(false); } finally { setZerodhaChecking(false); }
  };

  const handleZerodhaLogin = async () => {
    try {
      // Save API key/secret first if changed
      if (changes.zerodha_api_key || changes.zerodha_api_secret) {
        await settings.update({ zerodha_api_key: allSettings.zerodha_api_key, zerodha_api_secret: allSettings.zerodha_api_secret, zerodha_enabled: true });
        setChanges(prev => { const c = { ...prev }; delete c.zerodha_api_key; delete c.zerodha_api_secret; return c; });
      }
      const res = await zerodhaApi.loginUrl();
      const loginUrl = res.data?.data?.url;

      // Detect if mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        // On mobile, direct navigation works better than window.open()
        window.location.href = loginUrl;
      } else {
        // On desktop, open in new tab/window
        window.open(loginUrl, '_blank');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to get login URL. Enter API Key first.');
    }
  };

  const handleZerodhaConnect = async () => {
    if (!requestToken.trim()) { toast.error('Paste the request_token from the redirect URL'); return; }
    setZerodhaConnecting(true);
    try {
      const res = await zerodhaApi.connect(requestToken.trim());
      toast.success(res.data?.message || 'Zerodha connected!');
      setZerodhaConnected(true);
      setRequestToken('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Connection failed');
    } finally { setZerodhaConnecting(false); }
  };

  const handleZerodhaDisconnect = async () => {
    try {
      await zerodhaApi.disconnect();
      toast.success('Zerodha disconnected');
      setZerodhaConnected(false);
    } catch { toast.error('Disconnect failed'); }
  };

  const handleChange = (key, value) => {
    setAllSettings(prev => ({ ...prev, [key]: value }));
    setChanges(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settings.update(changes);
      toast.success('Settings saved successfully!');
      setChanges({});
    } catch { /* handled */ } finally { setSaving(false); }
  };

  const handleSwitchMode = async (mode) => {
    try {
      await bot.switchMode(mode);
      setBotMode(mode);
      toast.success(`Switched to ${mode} mode`);
    } catch { /* handled */ }
  };

  const [detectingTg, setDetectingTg] = useState(false);
  const [testingTg, setTestingTg] = useState(false);

  const handleAutoDetectGroup = async () => {
    setDetectingTg(true);
    try {
      const res = await notifApi.detectTelegramChatId();
      if (res.data?.success && res.data?.chatId) {
        toast.success(res.data.message || 'Telegram Group detected and saved!');
        handleChange('telegram_chat_id', res.data.chatId);
        handleChange('telegram_enabled', true);
      } else {
        toast.error(res.data?.message || 'No group messages found. Please send a message in the group and retry.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-detection failed');
    } finally {
      setDetectingTg(false);
    }
  };

  const handleTestTelegram = async () => {
    setTestingTg(true);
    try {
      const res = await notifApi.testTelegram({ chatId: allSettings.telegram_chat_id });
      if (res.data?.success) {
        toast.success('Test notification sent to Telegram group successfully!');
      } else {
        toast.error(res.data?.message || 'Failed to send Telegram test message');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send Telegram test message');
    } finally {
      setTestingTg(false);
    }
  };

  const s = allSettings || {};

  const tabs = ['Trading', 'Strategy', 'Risk Management', 'Broker', 'Notifications', 'System', 'Zerodha', 'Account'];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={60} /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Settings</Typography>
        {Object.keys(changes).length > 0 && (
          <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : `Save ${Object.keys(changes).length} Changes`}
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        {tabs.map((t, i) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* Trading Settings */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Trading Mode</Typography>
                {s.trading_mode === 'LIVE' && <Alert severity="warning" sx={{ mb: 2 }}>LIVE mode uses real money. Be careful!</Alert>}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Select Trading Mode:</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button variant={botMode === 'PAPER' ? 'contained' : 'outlined'} onClick={() => handleSwitchMode('PAPER')} color="info">📋 Paper Trading</Button>
                  <Button variant={botMode === 'LIVE' ? 'contained' : 'outlined'} onClick={() => handleSwitchMode('LIVE')} color="error">💰 Live Trading</Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>Market Data Provider:</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Data Source"
                  value={s.market_data_source || 'YAHOO'}
                  onChange={(e) => handleChange('market_data_source', e.target.value)}
                  helperText={
                    s.market_data_source === 'YAHOO'
                      ? '🟣 Yahoo Finance: Free, public real-time quotes without daily tokens or logins.'
                      : '🟢 Zerodha Kite: Official broker tick depth. Requires daily active Kite token.'
                  }
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="YAHOO">🟣 Yahoo Finance (Free Public API — No Token Required)</MenuItem>
                  <MenuItem value="ZERODHA">🟢 Zerodha KiteConnect (Requires Daily Token)</MenuItem>
                </TextField>
                <Divider sx={{ mb: 2 }} />
                <SettingField label="Bot Enabled" settingKey="bot_enabled" value={s.bot_enabled} type="boolean" onChange={handleChange} description="Master on/off switch for the bot" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Capital Settings</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><SettingField label="Live Capital (Zerodha)" settingKey="trading_capital" value={s.trading_capital} type="number" onChange={handleChange} unit="₹" description="Your Zerodha wallet balance for live trades" /></Grid>
                  <Grid item xs={12} sm={6}><SettingField label="Paper Capital (Virtual)" settingKey="paper_capital" value={s.paper_capital} type="number" onChange={handleChange} unit="₹" description="Virtual capital for paper/simulation trades" /></Grid>
                  <Grid item xs={6}><SettingField label="Max Trades/Day" settingKey="max_trades_per_day" value={s.max_trades_per_day} type="number" onChange={handleChange} /></Grid>
                  <Grid item xs={6}><SettingField label="Max Open Positions" settingKey="max_open_positions" value={s.max_open_positions} type="number" onChange={handleChange} /></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Stop Loss & Auto-Trailing</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}><SettingField label="Stop Loss" settingKey="stop_loss_pct" value={s.stop_loss_pct} type="number" onChange={handleChange} unit="%" description="Initial stop loss % below entry price" /></Grid>
                  <Grid item xs={12}><SettingField label="Reference Target" settingKey="target3_pct" value={s.target3_pct} type="number" onChange={handleChange} unit="%" description="Used only for the Risk:Reward number shown in the signal log — does not trigger an exit" /></Grid>
                </Grid>
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1.5, border: '1px solid #bbdefb' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    🛡️ <b>Auto step-trailing is always on</b> — no extra config needed. SL starts at Stop Loss % below entry, ratchets up automatically as price rises, and locks to breakeven once profit reaches 20%. Applies to options trades and NSE stock buys alike.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Auto Square Off</Typography>
                <SettingField label="Auto Square Off" settingKey="auto_square_off_enabled" value={s.auto_square_off_enabled} type="boolean" onChange={handleChange} description="Auto exit positions before market close" />
                <SettingField label="Minutes Before Close" settingKey="auto_square_off_mins" value={s.auto_square_off_mins} type="number" onChange={handleChange} unit="min" description="Square off N minutes before close" />
                <Divider sx={{ my: 2 }} />
                <SettingField label="Consec. Loss Limit" settingKey="consecutive_loss_limit" value={s.consecutive_loss_limit} type="number" onChange={handleChange} description="Stop after N consecutive losses" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, border: (s.equity_fixed_target_enabled === true || s.equity_fixed_target_enabled === 'true') ? '1px solid #81c784' : '1px solid #e0e0e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>Fixed Profit Target Exit (Per Stock)</Typography>
                  {(s.equity_fixed_target_enabled === true || s.equity_fixed_target_enabled === 'true') ? (
                    <Chip label={`TARGET ACTIVE (₹${s.equity_fixed_target_amount || 100}/stock)`} color="success" size="small" sx={{ fontWeight: 700 }} />
                  ) : (
                    <Chip label="DISABLED" size="small" variant="outlined" />
                  )}
                </Box>
                <SettingField
                  label="Enable Fixed Profit Target"
                  settingKey="equity_fixed_target_enabled"
                  value={s.equity_fixed_target_enabled}
                  type="boolean"
                  onChange={handleChange}
                  description="Auto-sell individual stock position when its profit reaches ₹X (Equity shares only, not NIFTY/BANKNIFTY)"
                />
                <Divider sx={{ my: 1.5 }} />
                <SettingField
                  label="Target Profit Per Stock Position"
                  settingKey="equity_fixed_target_amount"
                  value={s.equity_fixed_target_amount ?? 100}
                  type="number"
                  onChange={handleChange}
                  unit="₹"
                  description="Profit in ₹ for each stock position to trigger auto-sell (Default: ₹100). Applies only to shares."
                />
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    🎯 <b>Stock Target Exit:</b> When enabled, if <code>(Current Price - Buy Price) × Quantity ≥ ₹{s.equity_fixed_target_amount || 100}</code>, the individual stock is automatically sold to lock in profit. (Applies only to cash equity shares, NOT NIFTY/BANKNIFTY options).
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Master Global Auto-Trading Control */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2.5, border: (s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false') ? '2px solid #2e7d32' : '2px solid #d32f2f', bgcolor: (s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false') ? '#f1f8e9' : '#ffebee' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={800} color={s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false' ? '#1b5e20' : '#b71c1c'}>
                      🌐 MASTER GLOBAL AUTO-TRADING CONTROL
                    </Typography>
                  </Box>
                  <Chip
                    label={s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false' ? 'SYSTEM AUTO-TRADING ACTIVE' : 'ALL AUTO-TRADING PAUSED'}
                    color={s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false' ? 'success' : 'error'}
                    sx={{ fontWeight: 800 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Main system-wide kill switch. When OFF, all automated trade execution (Senior Trader AI Agent, Stock Screener Auto-Trader, and Equity Engine) is completely stopped across the entire platform.
                </Typography>
                <SettingField
                  label="Master Auto-Trading Switch"
                  settingKey="master_auto_trade_enabled"
                  value={s.master_auto_trade_enabled !== false && s.master_auto_trade_enabled !== 'false'}
                  type="boolean"
                  onChange={handleChange}
                  description="Global Master Switch — Enable or disable ALL automated trading engines simultaneously"
                />
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2, bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
              <Typography variant="subtitle2" fontWeight={800}>🛡️ Multi-Strategy Conflict Prevention Guarantee</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                The 3 strategies below (<b>Senior Trader AI Agent</b>, <b>NSE Stock Screener</b>, and <b>NSE Equity Engine</b>) run independently with isolated capital rules. They <b>NEVER conflict with each other</b> because the system's central <code>DuplicateTradeGuard</code> prevents double-buying or holding duplicate orders for the same symbol. You can turn any strategy ON or OFF below based on your preference.
              </Typography>
            </Alert>
          </Grid>

          {/* Senior Trader AI Agent Auto-Trading Toggle */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, border: (s.ai_agent_auto_trade_enabled !== false && s.ai_agent_auto_trade_enabled !== 'false') ? '1px solid #81c784' : '1px solid #e0e0e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>🧠 Senior Trader AI Agent Auto-Trading</Typography>
                  {(s.ai_agent_auto_trade_enabled !== false && s.ai_agent_auto_trade_enabled !== 'false') ? (
                    <Chip label="AGENT AUTO-ORDER ON" color="success" size="small" sx={{ fontWeight: 700 }} />
                  ) : (
                    <Chip label="DECISIONS ONLY (AUTO-ORDER OFF)" color="default" size="small" sx={{ fontWeight: 700 }} />
                  )}
                </Box>
                <Alert severity={s.ai_agent_auto_trade_enabled !== false && s.ai_agent_auto_trade_enabled !== 'false' ? 'info' : 'warning'} sx={{ mb: 2 }}>
                  {s.ai_agent_auto_trade_enabled !== false && s.ai_agent_auto_trade_enabled !== 'false'
                    ? 'When enabled, high-confidence Senior Trader AI Agent decisions (≥65% score) are automatically passed to risk management and executed as paper/live orders.'
                    : 'When disabled, Senior Trader AI Agent continues generating market analysis and trade decisions, but WILL NOT place any automatic orders.'}
                </Alert>
                <SettingField
                  label="Enable Senior Trader AI Auto-Orders"
                  settingKey="ai_agent_auto_trade_enabled"
                  value={s.ai_agent_auto_trade_enabled !== false && s.ai_agent_auto_trade_enabled !== 'false'}
                  type="boolean"
                  onChange={handleChange}
                  description="Toggle automated order execution for Senior Trader AI Agent trade decisions"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, border: (s.equity_auto_buy_enabled === true || s.equity_auto_buy_enabled === 'true') ? '1px solid #ef9a9a' : '1px solid #e0e0e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>NSE Equity Auto-Buy (AI Engine)</Typography>
                  {(s.equity_auto_buy_enabled === true || s.equity_auto_buy_enabled === 'true') && (
                    <Chip label="LIVE AUTO-BUY ON" color="error" size="small" />
                  )}
                </Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  When enabled, the AI Equity Engine will automatically place buy orders (paper or live depending on Trading Mode) when confidence ≥ {s.equity_min_confidence_pct || 90}%. All 5 guards must pass before any order is placed.
                </Alert>
                <SettingField
                  label="Enable Auto-Buy"
                  settingKey="equity_auto_buy_enabled"
                  value={s.equity_auto_buy_enabled}
                  type="boolean"
                  onChange={handleChange}
                  description="Auto-purchase stocks when AI engine confidence ≥ min threshold. Requires AI Equity Engine to be enabled."
                />
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SettingField
                      label="Max Auto-Buys Per Day"
                      settingKey="equity_auto_buy_max_per_day"
                      value={s.equity_auto_buy_max_per_day}
                      type="number"
                      onChange={handleChange}
                      description="Hard daily cap on auto-purchases across all symbols"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SettingField
                      label="Max Capital Per Trade"
                      settingKey="equity_auto_buy_capital_per_trade"
                      value={s.equity_auto_buy_capital_per_trade}
                      type="number"
                      onChange={handleChange}
                      unit="₹"
                      description="Max ₹ deployed per auto-buy (quantity is capped to fit this limit)"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1.5, border: '1px solid #ffe0b2' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    <b>5 Guards before any order fires:</b> (1) Auto-buy enabled · (2) No existing open/pending order for that symbol · (3) Not already bought that symbol today · (4) Open positions &lt; Max Open Positions · (5) Daily auto-buy count &lt; Max Per Day · (6) Available capital ≥ 1 share. All must pass.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, border: (s.stock_screener_auto_trade_enabled === true || s.stock_screener_auto_trade_enabled === 'true') ? '1px solid #ef9a9a' : '1px solid #e0e0e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight={600}>NSE Stock Recommendations — Auto Buy/Sell (Capital Strategy)</Typography>
                  {(s.stock_screener_auto_trade_enabled === true || s.stock_screener_auto_trade_enabled === 'true') && (
                    <Chip label="AUTO-TRADE ON" color="error" size="small" />
                  )}
                </Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  When enabled, only STRONG BUY rated stocks (score ≥ {s.stock_screener_min_score || 75}) from the Dashboard's NSE Stock Recommendations screener are auto-purchased (paper or live depending on Trading Mode), sized by risk against the capital below — completely separate from the AI Equity Engine above.
                </Alert>
                <SettingField
                  label="Enable Auto Buy/Sell"
                  settingKey="stock_screener_auto_trade_enabled"
                  value={s.stock_screener_auto_trade_enabled}
                  type="boolean"
                  onChange={handleChange}
                  description="Auto-purchase STRONG BUY stocks from the recommendations screener, sized by the capital & risk settings below"
                />
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Allocated Capital" settingKey="stock_screener_capital" value={s.stock_screener_capital}
                      type="number" onChange={handleChange} unit="₹"
                      description="Soft budget for this strategy — separate from the AI Equity Engine's own capital"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Risk Per Trade" settingKey="stock_screener_risk_per_trade_pct" value={s.stock_screener_risk_per_trade_pct}
                      type="number" onChange={handleChange} unit="%"
                      description="% of allocated capital risked per trade — drives position size (classic 1% rule)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Max Capital Per Trade" settingKey="stock_screener_max_capital_per_trade_pct" value={s.stock_screener_max_capital_per_trade_pct}
                      type="number" onChange={handleChange} unit="%"
                      description="Diversification cap — no single stock exceeds this % of allocated capital"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Min Score to Buy" settingKey="stock_screener_min_score" value={s.stock_screener_min_score}
                      type="number" onChange={handleChange}
                      description="75 = STRONG BUY threshold. Raise for a stricter filter."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Max Positions" settingKey="stock_screener_max_positions" value={s.stock_screener_max_positions}
                      type="number" onChange={handleChange}
                      description="Max concurrent auto-bought positions"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Max Trades/Day" settingKey="stock_screener_max_trades_per_day" value={s.stock_screener_max_trades_per_day}
                      type="number" onChange={handleChange}
                      description="Hard daily cap on new auto-buys"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Max Daily Loss" settingKey="stock_screener_max_daily_loss_pct" value={s.stock_screener_max_daily_loss_pct}
                      type="number" onChange={handleChange} unit="%"
                      description="Circuit breaker — halts new buys once today's realized loss reaches this % of capital"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Product" settingKey="stock_screener_product" value={s.stock_screener_product}
                      onChange={handleChange}
                      description="CNC (delivery, default) or MIS (intraday)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Exclude High P/E (>50)" settingKey="exclude_high_pe_stocks" value={s.exclude_high_pe_stocks}
                      type="boolean" onChange={handleChange}
                      description="Safety Shield: Prevents buying expensive shares that trigger Zerodha critical scrip nudges"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Fixed Target Exit" settingKey="equity_fixed_target_enabled" value={s.equity_fixed_target_enabled}
                      type="boolean" onChange={handleChange}
                      description="Auto-sell stock when profit hits target (Shares only)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <SettingField
                      label="Fixed Target (₹/stock)" settingKey="equity_fixed_target_amount" value={s.equity_fixed_target_amount ?? 100}
                      type="number" onChange={handleChange} unit="₹"
                      description="Target profit amount in ₹ per stock position (Default: 100)"
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <SettingField
                  label="Enable Smart Exit"
                  settingKey="stock_screener_smart_exit_enabled"
                  value={s.stock_screener_smart_exit_enabled}
                  type="boolean"
                  onChange={handleChange}
                  description="Exit early if the score deteriorates badly, instead of only waiting for the hard stop-loss"
                />
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <SettingField
                      label="Smart Exit Score" settingKey="stock_screener_smart_exit_score" value={s.stock_screener_smart_exit_score}
                      type="number" onChange={handleChange}
                      description="Score at/below which an open position is exited on signal reversal"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1.5, border: '1px solid #ffe0b2' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    <b>Strategy:</b> Only STRONG BUY signals qualify. Position size = risk-based (constant ₹ risked per trade, capped by both a max-capital-per-trade % and total available capital) — never a fixed lot size. Never doubles up on a symbol already held by any feature. Daily loss circuit breaker halts new entries for the day. Existing positions keep the same trailing stop-loss/target protection as manual trades, plus an early "smart exit" if the signal itself reverses.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Strategy Settings */}
      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>EMA Settings</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}><SettingField label="EMA Fast Period" settingKey="ema_fast_period" value={s.ema_fast_period} type="number" onChange={handleChange} description="Default: 9" /></Grid>
                  <Grid item xs={6}><SettingField label="EMA Slow Period" settingKey="ema_slow_period" value={s.ema_slow_period} type="number" onChange={handleChange} description="Default: 21" /></Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>RSI Settings</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}><SettingField label="RSI Period" settingKey="rsi_period" value={s.rsi_period} type="number" onChange={handleChange} /></Grid>
                  <Grid item xs={4}><SettingField label="RSI Buy Threshold" settingKey="rsi_buy_threshold" value={s.rsi_buy_threshold} type="number" onChange={handleChange} /></Grid>
                  <Grid item xs={4}><SettingField label="RSI Sell Threshold" settingKey="rsi_sell_threshold" value={s.rsi_sell_threshold} type="number" onChange={handleChange} /></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Filters</Typography>
                <SettingField label="VWAP Filter" settingKey="vwap_enabled" value={s.vwap_enabled} type="boolean" onChange={handleChange} description="Price must be above/below VWAP" />
                <SettingField label="Volume Filter" settingKey="volume_filter_enabled" value={s.volume_filter_enabled} type="boolean" onChange={handleChange} />
                {s.volume_filter_enabled && <SettingField label="Volume Multiplier" settingKey="volume_multiplier" value={s.volume_multiplier} type="number" onChange={handleChange} description="Volume must be N x average" />}
                <SettingField label="OI Filter" settingKey="oi_filter_enabled" value={s.oi_filter_enabled} type="boolean" onChange={handleChange} description="Open Interest analysis" />
                <SettingField label="ATR Filter" settingKey="atr_filter_enabled" value={s.atr_filter_enabled} type="boolean" onChange={handleChange} description="Average True Range filter" />
                <Divider sx={{ my: 2 }} />
                <SettingField label="Trade NIFTY" settingKey="nifty_enabled" value={s.nifty_enabled} type="boolean" onChange={handleChange} />
                <SettingField label="Trade BANKNIFTY" settingKey="banknifty_enabled" value={s.banknifty_enabled} type="boolean" onChange={handleChange} />
                <Divider sx={{ my: 2 }} />
                <SettingField label="Trade on Expiry Day" settingKey="trade_on_expiry_day" value={s.trade_on_expiry_day} type="boolean" onChange={handleChange} description="Allow trading on Thursday (weekly expiry) — high theta risk, use with caution" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Risk Management */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Risk Parameters</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>These settings control maximum risk exposure per trade and per day.</Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}><SettingField label="Risk Per Trade" settingKey="risk_per_trade_pct" value={s.risk_per_trade_pct} type="number" onChange={handleChange} unit="%" description="Max loss per trade as % of capital (Default: 1%)" /></Grid>
                  <Grid item xs={12}><SettingField label="Daily Loss Limit" settingKey="daily_loss_limit_pct" value={s.daily_loss_limit_pct} type="number" onChange={handleChange} unit="%" description="Stop trading when daily loss hits this % (Default: 2%)" /></Grid>
                  <Grid item xs={12}><SettingField label="Weekly Loss Limit" settingKey="weekly_loss_limit_pct" value={s.weekly_loss_limit_pct} type="number" onChange={handleChange} unit="%" description="Stop trading when cumulative weekly loss hits this % (Default: 7%)" /></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Paper Trading Simulation</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}><SettingField label="Brokerage Per Lot" settingKey="simulated_brokerage_per_lot" value={s.simulated_brokerage_per_lot} type="number" onChange={handleChange} unit="₹" /></Grid>
                  <Grid item xs={12}><SettingField label="Slippage" settingKey="simulated_slippage_pct" value={s.simulated_slippage_pct} type="number" onChange={handleChange} unit="%" description="Simulated price impact" /></Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Broker Settings (Zerodha) */}
      {tab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>Zerodha API is configured in the Zerodha tab. Live trading uses Zerodha connection only.</Alert>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Active Broker</Typography>
                <Typography variant="body2" color="success.main">✅ Zerodha (Kite Connect)</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Configure your Zerodha API credentials in the Zerodha tab above.</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Notification Settings */}
      {tab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Telegram Group Alerts</Typography>
                <SettingField label="Enable Telegram" settingKey="telegram_enabled" value={s.telegram_enabled} type="boolean" onChange={handleChange} />
                <SettingField label="Bot Token" settingKey="telegram_bot_token" value={s.telegram_bot_token} onChange={handleChange} description="Bot: @JigendraTradingAlerts_bot" />
                <Box sx={{ mt: 1.5, mb: 1.5 }}>
                  <SettingField label="Group Chat ID" settingKey="telegram_chat_id" value={s.telegram_chat_id} onChange={handleChange} description="Group ID (starts with - or -100)" />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleAutoDetectGroup}
                      disabled={detectingTg}
                    >
                      {detectingTg ? 'Detecting...' : 'Auto-Detect Group ID'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleTestTelegram}
                      disabled={testingTg || !s.telegram_chat_id}
                    >
                      {testingTg ? 'Sending...' : 'Test Send'}
                    </Button>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Alert Events</Typography>
                <SettingField label="Trade Executed" settingKey="notify_trade_executed" value={s.notify_trade_executed} type="boolean" onChange={handleChange} />
                <SettingField label="Target Hit" settingKey="notify_target_hit" value={s.notify_target_hit} type="boolean" onChange={handleChange} />
                <SettingField label="Stop Loss Hit" settingKey="notify_stoploss_hit" value={s.notify_stoploss_hit} type="boolean" onChange={handleChange} />
                <SettingField label="Daily Summary" settingKey="notify_daily_summary" value={s.notify_daily_summary} type="boolean" onChange={handleChange} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Auto Reports</Typography>
                <SettingField label="Daily Report" settingKey="daily_report_enabled" value={s.daily_report_enabled} type="boolean" onChange={handleChange} description="Auto-generate at 4 PM IST" />
                <SettingField label="Weekly Report" settingKey="weekly_report_enabled" value={s.weekly_report_enabled} type="boolean" onChange={handleChange} description="Auto-generate every Monday 8 AM" />
                <SettingField label="Monthly Report" settingKey="monthly_report_enabled" value={s.monthly_report_enabled} type="boolean" onChange={handleChange} description="Auto-generate on 1st of month" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* System Settings */}
      {tab === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>System Information</Typography>
                {[
                  { label: 'Signal Timeframe', key: 'signal_candle_timeframe', value: s.signal_candle_timeframe },
                  { label: 'NIFTY Lot Size', value: '50 (system default)' },
                  { label: 'BANKNIFTY Lot Size', value: '15 (system default)' },
                ].map(item => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, border: s.maintenance_mode === 'true' || s.maintenance_mode === true ? '1px solid #e57373' : 'inherit' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Maintenance Mode</Typography>
                <SettingField
                  label="Maintenance Mode"
                  settingKey="maintenance_mode"
                  type="boolean"
                  value={s.maintenance_mode === 'true' || s.maintenance_mode === true}
                  onChange={handleChange}
                  description="When on, only admin accounts can use the platform — everyone else is blocked at the API level. You (admin) are never locked out."
                />
                {(s.maintenance_mode === 'true' || s.maintenance_mode === true) && (
                  <Alert severity="warning" sx={{ mt: 1 }}>Maintenance mode is currently ON.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Zerodha Settings */}
      {tab === 6 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>Zerodha KiteConnect</Typography>
                  {zerodhaChecking ? (
                    <CircularProgress size={20} />
                  ) : zerodhaConnected ? (
                    <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />
                  ) : (
                    <Chip icon={<Cancel />} label="Not Connected" color="default" size="small" />
                  )}
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                  Enter your Zerodha KiteConnect API credentials below. The bot uses these to fetch real NIFTY/BANKNIFTY spot prices, option chains, and historical candles for paper trading.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SettingField
                      label="Enable Zerodha"
                      settingKey="zerodha_enabled"
                      value={s.zerodha_enabled}
                      type="boolean"
                      onChange={handleChange}
                      description="Use Zerodha as primary market data source"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SettingField
                      label="API Key"
                      settingKey="zerodha_api_key"
                      value={s.zerodha_api_key}
                      onChange={handleChange}
                      description="From Zerodha Developer Console → Apps"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SettingField
                      label="API Secret"
                      settingKey="zerodha_api_secret"
                      value={s.zerodha_api_secret}
                      type="password"
                      onChange={handleChange}
                      description="Kept secure in the database"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SettingField
                      label="Redirect URL (set this in Kite console)"
                      settingKey="zerodha_redirect_url"
                      value={s.zerodha_redirect_url || `${window.location.origin}/api/zerodha/callback`}
                      onChange={handleChange}
                      description="Copy this URL into your Zerodha app's Redirect URL field"
                    />
                  </Grid>
                </Grid>

                {Object.keys(changes).some(k => ['zerodha_api_key','zerodha_api_secret','zerodha_enabled'].includes(k)) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Save your API Key and Secret before logging in.
                  </Alert>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Step 1 — Login with Zerodha</Typography>
                {isMobileDevice ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Mobile device detected:</strong> Click the button below to open Zerodha. After login, you'll be redirected back here automatically.
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click the button below to open Zerodha's login page in a new tab. After login, you'll be redirected back automatically.
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<LinkIcon />}
                  onClick={handleZerodhaLogin}
                  sx={{ mb: 3, bgcolor: '#ff6600', '&:hover': { bgcolor: '#e55a00' } }}
                >
                  Login with Zerodha
                </Button>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Step 2 — Enter Request Token {isMobileDevice ? '(Mobile)' : '(If needed)'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {isMobileDevice ? (
                    <>
                      If you don't automatically redirect back after login, check your browser's address bar — it should show a URL ending with <code>?request_token=xxx</code>. Copy only the token value (the part after <code>request_token=</code>) and paste it below.
                    </>
                  ) : (
                    <>
                      If the automatic redirect doesn't work, Zerodha redirects to <code>{window.location.origin}/api/zerodha/callback?request_token=xxx</code>. Copy only the token value and paste here.
                    </>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Request Token"
                    value={requestToken}
                    onChange={e => setRequestToken(e.target.value)}
                    placeholder="Paste request_token here..."
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleZerodhaConnect}
                    disabled={zerodhaConnecting || !requestToken.trim()}
                    sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
                  >
                    {zerodhaConnecting ? <CircularProgress size={20} color="inherit" /> : 'Connect'}
                  </Button>
                </Box>

                {zerodhaConnected && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="success.main">Zerodha is connected</Typography>
                        <Typography variant="body2" color="text.secondary">Real market data is active. Session resets at midnight.</Typography>
                      </Box>
                      <Button variant="outlined" color="error" startIcon={<LinkOff />} onClick={handleZerodhaDisconnect} size="small">
                        Disconnect
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 2, bgcolor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Setup Guide</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Follow these steps to connect Zerodha:</Typography>
                {[
                  { step: '1', title: 'Create a Kite App', desc: 'Go to developers.kite.trade → Apps → Create App. Choose type "Connect".' },
                  { step: '2', title: 'Set Redirect URL', desc: `In your app settings, set Redirect URL to:\n${window.location.origin}/api/zerodha/callback` },
                  { step: '3', title: 'Copy API Key & Secret', desc: 'From your app page, copy the API Key and API Secret into the fields on the left.' },
                  { step: '4', title: 'Save credentials', desc: 'Click the Save button, then click "Login with Zerodha".' },
                  { step: '5', title: 'Login daily', desc: 'Zerodha access tokens expire at midnight. You need to log in once every trading day.' },
                ].map(item => (
                  <Box key={item.step} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, mt: 0.2 }}>
                      {item.step}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 7 && <AccountTab />}

      {Object.keys(changes).length > 0 && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24 }}>
          <Button variant="contained" size="large" startIcon={<Save />} onClick={handleSave} disabled={saving}
            sx={{ boxShadow: '0 4px 12px rgba(25,118,210,0.4)' }}>
            {saving ? 'Saving...' : `Save ${Object.keys(changes).length} Changes`}
          </Button>
        </Box>
      )}
    </Box>
  );
}
